/**
 * This is a Popcornized version of the NotificationContainer
 * (composer/components/NotificationContainer.tsx) component.
 *
 * It's used to display some of the notifications in the composer.
 */

import type * as React from 'react'
import { useEffect, useMemo } from 'react'
import clsx from 'clsx'

import { Button, Flex, Notice, ZapIcon } from '@buffer-mono/popcorn'

import { useCta } from '~publish/hooks/useCta'
import { useAccount } from '~publish/contexts/AccountContext'
import { SEGMENT_NAMES } from '~publish/legacy/constants'
import { navigateToMigrationHub } from '~publish/legacy/upgrade-paths/helpersUpgradePaths'

import { NotificationTypes } from '../AppConstants'

import styles from './PopcornNotificationContainer.module.css'

const UPGRADE_PATH_NAME = 'queueLimit-upgrade'
const CTA = SEGMENT_NAMES.COMPOSER_QUEUE_LIMIT

const NOTIFICATION_VARIANT_MAP = {
  [NotificationTypes.ERROR]: 'error',
  [NotificationTypes.SUCCESS]: 'info',
  [NotificationTypes.INFO]: 'info',
} as const

interface NotificationItem {
  id: string
  type: string
  scope: string
  message: string
  showSoftAndHardCloseOptions?: boolean
  cta?: any
}

interface NotificationContainerProps {
  visibleNotifications: NotificationItem[]
  scope?: string | null
  notScopes?: string[]
  type?: string | null
  notTypes?: string[]
  className?: string
  style?: React.CSSProperties
}

const PopcornNotificationContainer = (
  args: NotificationContainerProps,
): JSX.Element => {
  const {
    visibleNotifications,
    scope = null,
    notScopes = [],
    type = null,
    notTypes = [],
    className = undefined,
    style = undefined,
  } = args

  const openUpgradeModal = useUpgradeModal()

  const cta = useCta(CTA as CTA, {
    upgradePathName: UPGRADE_PATH_NAME,
  })

  const notificationsToDisplay = useMemo(() => {
    let notificationsToDisplay = [...visibleNotifications]

    if (scope !== null) {
      notificationsToDisplay = notificationsToDisplay.filter(
        (notif) => notif.scope === scope,
      )
    }

    if (notScopes.length > 0) {
      notificationsToDisplay = notificationsToDisplay.filter(
        (notif) => !notScopes.includes(notif.scope),
      )
    }

    if (type !== null) {
      notificationsToDisplay = notificationsToDisplay.filter(
        (notif) => notif.type === type,
      )
    }

    if (notTypes.length > 0) {
      notificationsToDisplay = notificationsToDisplay.filter(
        (notif) => !notTypes.includes(notif.type),
      )
    }

    return notificationsToDisplay
  }, [scope, notScopes, type, notTypes, visibleNotifications])

  // Track the upgrade path view for the composer queue limit notification
  // This is done here because the notification is rendered in the composer
  useEffect(() => {
    if (
      scope?.startsWith('PROFILE_QUEUE_LIMIT-') &&
      notificationsToDisplay.length > 0
    ) {
      cta.viewed()
    }
  }, [cta, scope, notificationsToDisplay])

  if (notificationsToDisplay.length === 0) return <></>

  return (
    <>
      {notificationsToDisplay.map((notification) => {
        const variant = notification.cta?.isUpgradePath
          ? 'upgrade'
          : NOTIFICATION_VARIANT_MAP[
              notification.type as keyof typeof NOTIFICATION_VARIANT_MAP
            ]
        const buttonVariant = notification.cta?.isUpgradePath
          ? 'upgrade'
          : 'primary'
        const actionHandler = (e: React.MouseEvent<HTMLElement>): void => {
          e.preventDefault()

          if (!notification.cta?.isUpgradePath) {
            notification.cta?.action()
          } else {
            cta.clicked()
            openUpgradeModal({
              cta: CTA,
              upgradePathName: UPGRADE_PATH_NAME,
            })
          }
        }
        return (
          <Notice
            key={notification.id}
            id={notification.id}
            icon={false}
            variant={variant}
            className={clsx(styles.notice, className)}
            style={style}
          >
            <Flex
              justify="between"
              align="center"
              gap="xs"
              className={styles.noticeContent}
            >
              <Notice.Text
                dangerouslySetInnerHTML={{ __html: notification.message }}
              />
              {notification.cta && (
                <Notice.Actions>
                  <Button variant={buttonVariant} onClick={actionHandler}>
                    {notification.cta?.isUpgradePath ? <ZapIcon /> : null}
                    {notification.cta?.label}
                  </Button>
                </Notice.Actions>
              )}
            </Flex>
          </Notice>
        )
      })}
    </>
  )
}

interface OpenModalProps {
  cta: string
  ctaMPMigration?: string
  upgradePathName?: string
  shouldPickModalOnOrganizationState?: boolean
}
function useUpgradeModal(): (props: OpenModalProps) => void {
  const user = useAccount()
  const { isOneBufferOrganization } = user?.account?.currentOrganization || {}

  const openModal = ({
    cta,
    ctaMPMigration,
    upgradePathName,
    shouldPickModalOnOrganizationState = false,
  }: OpenModalProps): void => {
    if (isOneBufferOrganization) {
      const { MODALS, actions } = window?.appshell || {}
      actions.openModal(MODALS.subscriptionUpdate, {
        cta,
        shouldPickModalOnOrganizationState,
        upgradePathName,
      })
    } else {
      navigateToMigrationHub(ctaMPMigration)
    }
  }

  return openModal
}

export default PopcornNotificationContainer
